import { useCallback, useEffect, useRef, useState } from 'react'
import { useEvent } from './useEvent'
import anylogger from '@app/anylogger'

const log = anylogger('useMobileParams')

interface MobileParamResults {
	isPortrait: boolean
	addressBarVisible: boolean
	addressBarHeight?: number
}
/**
 *
 * @returns variables indicating the address bar visibility (only tested on android) and height, as well as isPortrait. Note that the addressbar height is only available once the address bar is hidden.
 * This only works on mobile devices as addressbar height and address bar visibility is not relevant on desktop.  This will return misleading values for desktop and should not be used.
 * ToDo: Should add a check to return undefined for address bar variables if not on mobile.
 */
export function useMobileParams(): MobileParamResults {
	// we have to start with the assumption that the address bar is visible, as we cannot know if it is hidden until we scroll, so this must be set to true.
	const [isVisible, setIsVisible] = useState<boolean>(true)
	const [isPortrait, setIsPortrait] = useState(true)
	const [addressBarHeight, setAddressBarHeight] = useState<number | undefined>(undefined)

	const viewportHeight = useRef<number>(0)
	const viewportWidth = useRef<number>(0)

	useEffect(() => {
		viewportHeight.current = Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
		viewportWidth.current = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
		setIsPortrait(viewportHeight.current > viewportWidth.current)
	}, [])
	const onResize = useCallback(() => {
		var newViewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
		var newViewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
		var hasOrientationChanged = newViewportHeight > newViewportWidth != isPortrait
		log('resize', newViewportHeight, viewportHeight.current, isPortrait, hasOrientationChanged)

		if (!hasOrientationChanged && newViewportHeight != viewportHeight.current) {
			// weirdly, we cannot know the address bar height until it is hidded, at which point window.innerHeight grows
			// on desktop, I don't think innerheight changes, so
			const abh = Math.abs(newViewportHeight - viewportHeight.current)
			if (abh != 0 && typeof addressBarHeight == 'undefined') setAddressBarHeight(abh)

			if (newViewportHeight < viewportHeight.current) {
				setIsVisible(true)
				// Android Chrome address bar has appeared
			} else {
				// Android Chrome address bar has disappeared
				setIsVisible(false)
			}
		} else if (hasOrientationChanged) {
			// Orientation change
		}

		viewportHeight.current = newViewportHeight
		viewportWidth.current = newViewportWidth
		setIsPortrait(viewportHeight.current > viewportWidth.current)
	}, [addressBarHeight, isPortrait])

	useEvent(window, 'resize', onResize)

	return { addressBarVisible: isVisible, addressBarHeight, isPortrait }
}
